/**
 * ResponsiveProTable — renders ProTable on desktop, MobileCardList on mobile.
 *
 * Drop-in replacement for @ant-design/pro-table.  Reads isPC from Redux internally.
 * Only the import line needs to change:
 *
 *   // before
 *   import ProTable from "@ant-design/pro-table";
 *   // after
 *   import ResponsiveProTable from "@components/ResponsiveProTable";
 *
 * JSX stays the same: <ResponsiveProTable request={fn} columns={cols} ... />
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import ProTable from "@ant-design/pro-table";
import MobileCardList from "../MobileCardList";

/* ========================================================
   Hook – call ProTable's request function and manage state
   ======================================================== */
function useProTableData(request) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const requestRef = useRef(request);
  requestRef.current = request;

  const fetchData = useCallback(
    async (page, size = pageSize) => {
      if (!requestRef.current) return;
      setLoading(true);
      try {
        const result = await requestRef.current({
          current: page,
          pageSize: size,
        });
        if (result) {
          setData(result.data || []);
          setTotal(result.total || 0);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error("ResponsiveProTable fetch error:", err);
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    fetchData(1, pageSize);
  }, [fetchData, pageSize]);

  const refresh = useCallback(() => fetchData(currentPage, pageSize), [
    fetchData, currentPage, pageSize,
  ]);

  const changePage = useCallback(
    (page) => fetchData(page, pageSize),
    [fetchData, pageSize]
  );

  return { data, loading, total, currentPage, pageSize, refresh, changePage };
}

/* ========================================================
   Component
   ======================================================== */
const ResponsiveProTable = (props) => {
  const isPC = useSelector((s) => s.App?.isPC ?? true);
  const {
    request, columns, rowKey, rowSelection, pagination,
    expandable, onRow, search, toolBarRender, options, actionRef,
    ...rest
  } = props;

  // ---- mobile data management ----
  const { data, loading, total, currentPage, pageSize, refresh, changePage } =
    useProTableData(request);

  // Expose reload via actionRef on mobile
  const mobileActionRef = useMemo(
    () => ({ reload: refresh, current: { getDataSource: () => data } }),
    [refresh, data]
  );

  useEffect(() => {
    if (actionRef && !isPC) {
      if (typeof actionRef === "function") {
        actionRef(mobileActionRef);
      } else if (actionRef.current !== undefined) {
        actionRef.current = mobileActionRef;
      }
    }
  }, [actionRef, isPC, mobileActionRef]);

  // Filter ProTable columns for mobile
  const mobileColumns = useMemo(() => {
    if (!columns) return [];
    return columns.filter((col) =>
      col.valueType !== "index" && col.valueType !== "indexBorder"
    );
  }, [columns]);

  /* ---- Desktop ---- */
  if (isPC) {
    return (
      <ProTable
        request={request}
        columns={columns}
        rowKey={rowKey}
        rowSelection={rowSelection}
        pagination={pagination}
        expandable={expandable}
        onRow={onRow}
        search={search}
        toolBarRender={toolBarRender}
        options={options}
        actionRef={actionRef}
        {...rest}
      />
    );
  }

  /* ---- Mobile ---- */
  const mobilePagination = pagination === false
    ? false
    : { current: currentPage, pageSize, total, onChange: changePage, showSizeChanger: false };

  return (
    <MobileCardList
      columns={mobileColumns}
      dataSource={data}
      loading={loading}
      rowKey={rowKey || "id"}
      rowSelection={rowSelection}
      pagination={mobilePagination}
      expandable={expandable}
      onRow={onRow}
      onRefresh={refresh}
      showToolbar={true}
      size="small"
      {...rest}
    />
  );
};

export default ResponsiveProTable;
